import React, { useState, useCallback, useEffect } from "react";
import { buildApiUrl } from "../utils/apiConfig";
import "../styles/google-drive-folder-browser.css";
import { apiClient } from "../utils/apiClient";

type DriveFile = {
    id: string;
    name: string;
    mimeType: string;
    size: number;
    createdTime?: string;
};

type FolderData = {
    folderId: string;
    folderName: string;
    files: DriveFile[];
    totalFiles: number;
};

interface GoogleDriveFolderBrowserProps {
    initialFolder?: FolderData;
    onClose: () => void;
    onImportFiles?: (fileIds: string[]) => void;
    onError?: (error: string) => void;
}

type BreadcrumbItem = {
    id: string;
    name: string;
};

const FOLDER_MIME = "application/vnd.google-apps.folder";

const GoogleDriveFolderBrowser: React.FC<GoogleDriveFolderBrowserProps> = ({
    initialFolder,
    onClose,
    onImportFiles,
    onError,
}) => {
    const [currentFolder, setCurrentFolder] = useState<FolderData | null>(initialFolder || null);
    const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>(
        initialFolder ? [{ id: initialFolder.folderId, name: initialFolder.folderName }] : []
    );

    useEffect(() => {
        if (initialFolder) {
            setCurrentFolder(initialFolder);
            setBreadcrumbs([{ id: initialFolder.folderId, name: initialFolder.folderName }]);
        }
    }, [initialFolder]);

    const fetchFolderContents = useCallback(async (folderId: string, folderName: string) => {
        setLoading(true);
        try {
            const response = await apiClient(buildApiUrl("api/user/drive/folder/select"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({ folderId, folderName }),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch folder contents: ${response.statusText}`);
            }

            const data: FolderData = await response.json();
            setCurrentFolder(data);
            setSelectedFileIds(new Set());
        } catch (error: any) {
            if (onError) {
                onError(error.message || "Failed to load folder contents");
            }
        } finally {
            setLoading(false);
        }
    }, [onError]);

    const handleFolderClick = useCallback((folder: DriveFile) => {
        const newBreadcrumb = { id: folder.id, name: folder.name };
        setBreadcrumbs(prev => [...prev, newBreadcrumb]);
        fetchFolderContents(folder.id, folder.name);
    }, [fetchFolderContents]);

    const handleBreadcrumbClick = useCallback((index: number) => {
        const clickedBreadcrumb = breadcrumbs[index];
        setBreadcrumbs(breadcrumbs.slice(0, index + 1));
        fetchFolderContents(clickedBreadcrumb.id, clickedBreadcrumb.name);
    }, [breadcrumbs, fetchFolderContents]);

    const handleFileSelect = useCallback((fileId: string) => {
        setSelectedFileIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fileId)) {
                newSet.delete(fileId);
            } else {
                newSet.add(fileId);
            }
            return newSet;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        if (!currentFolder) return;
        const allFileIds = currentFolder.files
            .filter(f => f.mimeType !== FOLDER_MIME)
            .map(f => f.id);
        setSelectedFileIds(new Set(allFileIds));
    }, [currentFolder]);

    const handleDeselectAll = useCallback(() => {
        setSelectedFileIds(new Set());
    }, []);

    const handleImport = useCallback(() => {
        if (onImportFiles && selectedFileIds.size > 0) {
            onImportFiles(Array.from(selectedFileIds));
            onClose();
        }
    }, [onImportFiles, selectedFileIds, onClose]);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const formatDate = (dateString?: string): string => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const getFileIcon = (mimeType: string): string => {
        if (mimeType === FOLDER_MIME) return "📁";
        if (mimeType.includes("image/")) return "🖼️";
        if (mimeType.includes("video/")) return "🎥";
        if (mimeType === "application/pdf") return "📄";
        if (mimeType === "application/vnd.google-apps.document") return "📝";
        if (mimeType === "application/vnd.google-apps.spreadsheet") return "📊";
        if (mimeType === "application/vnd.google-apps.presentation") return "📊";
        return "📄";
    };

    const getTotalSelectedSize = (): string => {
        if (!currentFolder) return "0 B";
        const totalBytes = currentFolder.files
            .filter(f => selectedFileIds.has(f.id))
            .reduce((sum, f) => sum + f.size, 0);
        return formatFileSize(totalBytes);
    };

    if (!currentFolder) {
        return null;
    }

    const folders = currentFolder.files.filter(f => f.mimeType === FOLDER_MIME);
    const files = currentFolder.files.filter(f => f.mimeType !== FOLDER_MIME);
    const allSelected = files.length > 0 && files.every(f => selectedFileIds.has(f.id));

    return (
        <div className="folder-browser-overlay">
            <div className="folder-browser-modal">
                {/* Header */}
                <div className="folder-browser-header">
                    <div className="folder-browser-title">
                        <h2>Browse Folder</h2>
                        <button
                            className="folder-browser-close"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>

                    {/* Breadcrumbs */}
                    <div className="folder-browser-breadcrumbs">
                        {breadcrumbs.map((crumb, index) => (
                            <React.Fragment key={crumb.id}>
                                {index > 0 && <span className="breadcrumb-separator">/</span>}
                                <button
                                    className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? "active" : ""}`}
                                    onClick={() => handleBreadcrumbClick(index)}
                                    disabled={index === breadcrumbs.length - 1}
                                >
                                    {crumb.name}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Actions Bar */}
                <div className="folder-browser-actions">
                    <div className="selection-actions">
                        <button
                            className="action-btn-secondary"
                            onClick={handleSelectAll}
                            disabled={files.length === 0}
                        >
                            Select All
                        </button>
                        <button
                            className="action-btn-secondary"
                            onClick={handleDeselectAll}
                            disabled={selectedFileIds.size === 0}
                        >
                            Deselect All
                        </button>
                    </div>
                    <div className="selection-info">
                        {selectedFileIds.size > 0 && (
                            <span>
                                {selectedFileIds.size} file{selectedFileIds.size !== 1 ? "s" : ""} selected
                                ({getTotalSelectedSize()})
                            </span>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="folder-browser-content">
                    {loading ? (
                        <div className="folder-browser-loading">
                            <div className="spinner"></div>
                            <p>Loading folder contents...</p>
                        </div>
                    ) : currentFolder.files.length === 0 ? (
                        <div className="folder-browser-empty">
                            <div className="empty-icon">📁</div>
                            <h3>This folder is empty</h3>
                            <p>No files or folders to display</p>
                        </div>
                    ) : (
                        <div className="folder-browser-list">
                            {/* Folders first */}
                            {folders.map(folder => (
                                <div
                                    key={folder.id}
                                    className="folder-item folder-clickable"
                                    onClick={() => handleFolderClick(folder)}
                                >
                                    <div className="file-icon-cell">{getFileIcon(folder.mimeType)}</div>
                                    <div className="file-name-cell">
                                        <span className="file-name">{folder.name}</span>
                                        <span className="file-type-badge">Folder</span>
                                    </div>
                                    <div className="file-size-cell">—</div>
                                    <div className="file-date-cell">{formatDate(folder.createdTime)}</div>
                                    <div className="file-action-cell">
                                        <span className="folder-arrow">→</span>
                                    </div>
                                </div>
                            ))}

                            {/* Files */}
                            {files.map(file => (
                                <div
                                    key={file.id}
                                    className={`folder-item ${selectedFileIds.has(file.id) ? "selected" : ""}`}
                                >
                                    <div className="file-checkbox-cell">
                                        <input
                                            type="checkbox"
                                            checked={selectedFileIds.has(file.id)}
                                            onChange={() => handleFileSelect(file.id)}
                                            aria-label={`Select ${file.name}`}
                                        />
                                    </div>
                                    <div className="file-icon-cell">{getFileIcon(file.mimeType)}</div>
                                    <div className="file-name-cell">
                                        <span className="file-name">{file.name}</span>
                                    </div>
                                    <div className="file-size-cell">{formatFileSize(file.size)}</div>
                                    <div className="file-date-cell">{formatDate(file.createdTime)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="folder-browser-footer">
                    <div className="footer-info">
                        <span>
                            {currentFolder.totalFiles} item{currentFolder.totalFiles !== 1 ? "s" : ""} total
                        </span>
                        {currentFolder.folderId && (
                            <a
                                href={`https://drive.google.com/drive/folders/${currentFolder.folderId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="view-in-drive-link"
                            >
                                View in Google Drive ↗
                            </a>
                        )}
                    </div>
                    <div className="footer-actions">
                        <button className="action-btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className="action-btn-primary"
                            onClick={handleImport}
                            disabled={selectedFileIds.size === 0}
                        >
                            Import Selected Files ({selectedFileIds.size})
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GoogleDriveFolderBrowser;

